import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import type {ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {MilestoneIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {useCallback, useMemo} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import {BulkIssueMilestonePicker} from '../bulk-pickers/BulkIssueMilestonePicker'
import type {SharedListHeaderActionProps} from '../types'
import type {ApplyMilestoneBulkActionQuery} from './__generated__/ApplyMilestoneBulkActionQuery.graphql'

type ApplyMilestoneActionProps = {
  issueIds: string[]
} & SharedListHeaderActionProps

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const ApplyMilestoneQuery = graphql`
  query ApplyMilestoneBulkActionQuery($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Issue {
        id
        milestone {
          id
          # eslint-disable-next-line relay/unused-fields
          title
          # eslint-disable-next-line relay/unused-fields
          closed
          # eslint-disable-next-line relay/unused-fields
          dueOn
          # eslint-disable-next-line relay/unused-fields
          progressPercentage
          # eslint-disable-next-line relay/unused-fields
          url
          # eslint-disable-next-line relay/unused-fields
          closedAt
        }
      }
    }
  }
`

export const ApplyMilestoneBulkAction = ({issueIds, disabled, nested, ...rest}: ApplyMilestoneActionProps) => {
  const {nodes: updatedIssueNodes} = useLazyLoadQuery<ApplyMilestoneBulkActionQuery>(
    ApplyMilestoneQuery,
    {ids: issueIds},
    {fetchPolicy: 'store-or-network'},
  )

  const selectedIssuesMilestones =
    (updatedIssueNodes || []).map(node => {
      const nodeId = node?.id
      if (!nodeId) return null
      const data = node.milestone ? node.milestone : null

      return data
    }) ?? []

  // If all selected issues have the same milestone, mark this milestone as selected in the picker.
  const activeMilestone =
    selectedIssuesMilestones.length > 0 &&
    selectedIssuesMilestones.every(milestone => milestone?.id === selectedIssuesMilestones[0]?.id)
      ? selectedIssuesMilestones[0] || null
      : null

  const anchorElement = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      const anchorText = 'Milestone'

      if (nested) {
        return (
          <ActionList.Item disabled={disabled} {...anchorProps} role="menuitem">
            <ActionList.LeadingVisual>
              <MilestoneIcon />
            </ActionList.LeadingVisual>
            {anchorText}
          </ActionList.Item>
        )
      }

      return (
        <Button
          data-testid={'bulk-set-milestone-button'}
          disabled={disabled}
          leadingVisual={MilestoneIcon}
          trailingVisual={TriangleDownIcon}
          {...anchorProps}
        >
          {anchorText}
        </Button>
      )
    },
    [disabled, nested],
  )

  const noMilestoneItem = useMemo(() => {
    const option: ExtendedItemProps<MilestonePickerMilestone> = {
      id: SPECIAL_VALUES.noMilestoneData.id,
      description: '',
      descriptionVariant: 'inline',
      /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string */
      children: <VerifiedHTMLText html={SPECIAL_VALUES.noMilestoneData.title as SafeHTMLString} />,
      source: SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestone,
      groupId: '',
      leadingVisual: () => <MilestoneIcon />,
    }
    return option
  }, [])

  return (
    <BulkIssueMilestonePicker
      readonly={false}
      activeMilestone={activeMilestone}
      anchorElement={anchorElement}
      nested={nested}
      noMilestoneItem={noMilestoneItem}
      canCreateMilestone={false}
      {...rest}
    />
  )
}

try{ ApplyMilestoneBulkAction.displayName ||= 'ApplyMilestoneBulkAction' } catch {}